import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography,
} from '@mui/material';
import { Add as AddIcon, Settings as SettingsIcon } from '@mui/icons-material';
import SearchableSelect from '../common/SearchableSelect';
import { SalesOrderService, fetchAllPages } from '../../services/salesOrder.service';
import { getStoresByInstitutionId } from '../../services/store.service';
import { CustomerService } from '../../services/customer.service';
import { Customer } from '../../interfaces/sales/customer.interface';
import { Store } from '../../interfaces/store.interface';
import {
  OrderSummary, SALES_ORDER_STATUS_COLORS, SalesOrderSettings, SalesOrderStatus,
} from '../../interfaces/sales/salesOrder.interface';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';
import SalespersonSelect, { salespersonParams } from './scope/SalespersonSelect';
import { formatFc, isForeignCurrency } from '../../utils/fcDocuments';   // G5b

const STATUSES: SalesOrderStatus[] = ['DRAFT', 'CONFIRMED', 'PARTIALLY_DELIVERED', 'DELIVERED', 'CLOSED', 'CANCELLED'];
const filterCell = { flex: '1 1 220px', maxWidth: 320 };

const SalesOrderList: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const canManage = usePermission(PERMISSIONS.SALES_ORDER_MANAGE);
  const canSettings = usePermission(PERMISSIONS.SALES_SETTINGS_MANAGE);
  const institutionId = AuthService.getSelectedInstitution();
  const [rows, setRows] = useState<OrderSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<string>('');
  const [customerId, setCustomerId] = useState<string>('');
  const [owner, setOwner] = useState<string | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    fetchAllPages<Customer>((p, n) => CustomerService.listByInstitution(institutionId, p, n, 'ALL')).then(setCustomers).catch(() => setCustomers([]));
  }, [institutionId]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await SalesOrderService.list(institutionId, {
        status: (status || undefined) as SalesOrderStatus | undefined, customerId: customerId || undefined, ...salespersonParams(owner), page, size,
      });
      setRows(res.content);
      setTotal(res.totalElements);
    } catch (e) {
      toast.showApiError(e, 'Failed to load sales orders');
    } finally {
      setLoading(false);
    }
  }, [institutionId, status, customerId, owner, page, size]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.orders.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Typography variant="h5">Sales Orders</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canSettings && <Button data-help="sales.orders.settings" startIcon={<SettingsIcon />} onClick={() => setSettingsOpen(true)}>Order settings</Button>}
          {canManage && (
            <Button data-help="sales.orders.new" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/sales/orders/new')}>New Order</Button>
          )}
        </Box>
      </Box>
      <Box data-help-section="sales.orders.filter" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <SearchableSelect
          label="Status" size="small" sx={filterCell} options={STATUSES.map(s => ({ id: s, name: humanizeEnum(s) }))}
          value={status || null} onChange={id => { setStatus(id); setPage(0); }} getOptionLabel={o => o.name} clearable
        />
        <SearchableSelect
          label="Customer" size="small" sx={filterCell} options={customers} value={customerId || null}
          onChange={id => { setCustomerId(id); setPage(0); }} getOptionLabel={c => c.name} clearable
        />
        <SalespersonSelect data-help="sales.orders.filter.salesperson"
          withUnassigned sx={filterCell} value={owner} onChange={id => { setOwner(id); setPage(0); }}
        />
      </Box>
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} data-help-section="sales.orders.table">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell data-help="sales.orders.col.number">Order #</TableCell><TableCell data-help="sales.orders.col.customer">Customer</TableCell><TableCell data-help="sales.orders.col.salesperson">Salesperson</TableCell><TableCell data-help="sales.orders.col.date">Date</TableCell>
                <TableCell data-help="sales.orders.col.delivery">Requested delivery</TableCell><TableCell align="right" data-help="sales.orders.col.total">Total</TableCell><TableCell data-help="sales.orders.col.status">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/sales/orders/${r.id}`)}>
                  <TableCell>{r.orderNo ?? 'Draft'}</TableCell>
                  <TableCell>{r.customerName ?? '—'}</TableCell>
                  <TableCell>{r.salespersonName ?? 'Unassigned'}</TableCell>
                  <TableCell>{formatDate(r.orderDate)}</TableCell>
                  <TableCell>
                    {r.requestedDeliveryDate ? formatDate(r.requestedDeliveryDate) : '—'}
                    {r.overdue && <Chip label="Overdue" color="error" size="small" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell align="right">{formatMoney(r.totalAmount)}{isForeignCurrency(r.currencyCode) && <Typography variant="caption" color="text.secondary" display="block">{formatFc(r.currencyCode!, r.foreignTotal)}</Typography>}</TableCell>
                  <TableCell>
                    <Chip label={humanizeEnum(r.status)} color={SALES_ORDER_STATUS_COLORS[r.status]} size="small" />
                    {r.fullyInvoiced && <Chip label="Invoiced" size="small" sx={{ ml: 1 }} />}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={7}>No sales orders yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
          <TablePagination
            component="div" count={total} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={size}
            onRowsPerPageChange={e => { setSize(Number(e.target.value)); setPage(0); }}
          />
        </TableContainer>
      )}
      {settingsOpen && <OrderSettingsDialog institutionId={institutionId} onClose={() => setSettingsOpen(false)} />}
    </Box>
  );
};

const OrderSettingsDialog: React.FC<{ institutionId: string; onClose: () => void }> = ({ institutionId, onClose }) => {
  const toast = useToast();
  const [settings, setSettings] = useState<SalesOrderSettings | null>(null);
  const [stores, setStores] = useState<Store[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    SalesOrderService.getSettings(institutionId).then(setSettings).catch(e => toast.showApiError(e, 'Failed to load settings'));
    fetchAllPages<Store>((p, n) => getStoresByInstitutionId(p, n, 'ALL')).then(setStores).catch(() => setStores([]));
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!settings) return;
    try {
      setSaving(true);
      await SalesOrderService.updateSettings(institutionId, settings);
      toast.showSuccess('Order settings saved');
      onClose();
    } catch (e) {
      toast.showApiError(e, 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs" data-help-page="sales.orders.settings.page">
      <DialogTitle>Sales order settings</DialogTitle>
      <DialogContent data-help-section="sales.orders.settings.section.form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {!settings ? <CircularProgress /> : (
          <>
            <SearchableSelect
              data-help="sales.orders.settings.defaultStore" label="Default delivery store" fullWidth options={stores} value={settings.defaultStoreId ?? null}
              onChange={id => setSettings({ ...settings, defaultStoreId: id || null })} getOptionLabel={s => s.storeName} clearable
            />
            <FormControlLabel data-help="sales.orders.settings.showPrices"
              control={<Checkbox checked={settings.showPricesOnChallan} onChange={e => setSettings({ ...settings, showPricesOnChallan: e.target.checked })} />}
              label="Show prices on the delivery challan"
            />
            <FormControlLabel data-help="sales.orders.settings.allowDirectInvoice"
              control={<Checkbox checked={settings.allowInvoiceWithoutOrder} onChange={e => setSettings({ ...settings, allowInvoiceWithoutOrder: e.target.checked })} />}
              label="Allow invoices that are not built from delivery challans"
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button data-help="sales.orders.settings.cancel" onClick={onClose}>Cancel</Button>
        <Button data-help="sales.orders.settings.save" variant="contained" onClick={save} disabled={!settings || saving}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SalesOrderList;
